import React, { useMemo, useCallback } from 'react';
import classNames from 'classnames';
import { uniqBy } from 'ramda';
import { Select, SelectableOption } from '@moda/om';
import { LineItem } from '../../../hooks/useCart/cart.rest';
import './LineItemQuantitySelector.scss';

type Props = {
  item: LineItem;
  onChange: (quantity: number) => void;
};

export const LineItemQuantitySelector: React.FC<Props> = ({
  item: {
    type,
    attributes: { skuId, quantity: selectedQuantity, inventory, isProductAvailable, error }
  },
  onChange
}) => {
  const selectedInventory = useMemo(
    () => inventory.find(({ id }) => id === skuId) || inventory[0],
    [skuId, inventory]
  );

  const quantities: SelectableOption[] = useMemo(
    () =>
      uniqBy(
        quantity => quantity.value,
        [
          ...Array.from({ length: selectedInventory?.available || 0 }, (_, index) => ({
            label: `${index + 1}`,
            value: `${index + 1}`
          })),
          { label: `${selectedQuantity}`, value: `${selectedQuantity}`, disabled: true }
        ]
      ),
    [selectedInventory, selectedQuantity]
  );

  const handleQuantityChange = useCallback(
    (quantity: string) => Number(quantity) !== selectedQuantity && onChange(Number(quantity)),
    [onChange, selectedQuantity]
  );

  return (
    <Select
      className={classNames('LineItemQuantitySelector', {
        'LineItemQuantitySelector--error': error?.type === 'SHORT_QUANTITY_EXCEEDED_MESSAGE'
      })}
      disabled={type === 'cart_items' && !isProductAvailable}
      options={quantities}
      onChange={handleQuantityChange}
      value={selectedQuantity.toString()}
      label="Qty"
      data-testid="lineItemQuantitySelector"
      idRef={`${skuId}-${selectedQuantity}`}
    />
  );
};
